/**
 * Production Progress Component
 * 
 * Multi-stage progress display for production template generation.
 * Shows all 9 pipeline stages with:
 * - Checklist-style stage indicators (✓ done, ⏳ current, ⏸️ pending)
 * - Overall progress bar (0-100%)
 * - Current stage message
 * - Elapsed time
 * - ETA (estimated time remaining)
 * - Real-time metadata stats (criteria count, question count, etc.)
 */

import React, { useState } from 'react';
import { CheckCircle, Circle, Clock, Loader2, Sparkles, Target, Box, FileText, CheckSquare, List, MessageSquare, FileCode, FileCheck, Users, AlertCircle, RefreshCw, Scale, Layers } from 'lucide-react';

export type ProductionTemplateStage =
  | 'idle'
  | 'queued'
  | 'parsing'
  | 'structure_extraction'
  | 'chunking'
  | 'criteria_extraction'
  | 'criteria_normalization'
  | 'section_mapping'
  | 'staff_requirements_extraction'
  | 'company_requirements_extraction'
  | 'question_generation'
  | 'staff_company_question_generation'
  | 'template_building'
  | 'markdown_rendering'
  | 'complete'
  | 'error';

interface ProductionProgressMetadata {
  criteria_count?: number;
  question_count?: number;
  section_count?: number;
  chunk_count?: number;
  quality_score?: number;
  // Sub-progress tracking
  total_criteria?: number;
  completed?: number;
  completed_criteria?: number;
  current_criterion?: string;
  current_criterion_id?: string;
  current_criterion_title?: string;
  current_title?: string;
  // Type breakdowns
  gate?: number;
  scored?: number;
  gate_count?: number;
  scored_count?: number;
  gate_ids?: number;
  scored_ids?: number;
  // Other details
  avg_per_criterion?: number;
  page_count?: number;
  char_count?: number;
  artifact_count?: number;
  total_sections?: number;
  markdown_length?: number;
  line_count?: number;
  // Multi-document parsing (NEW)
  document_count?: number;
  filenames?: string[];
  // Mapping stats
  total_mappings?: number;
  avg_sections_per_criterion?: number;
  // Staff & company requirements
  staff_requirements_count?: number;
  company_requirements_count?: number;
  staff_questions_count?: number;
  company_questions_count?: number;
  capability_statements_count?: number;
  past_projects_enumeration_count?: number;
  // Structure extraction
  mandatory_count?: number;
  rated_count?: number;
  mandatory_criteria_count?: number;
  rated_criteria_count?: number;
  // Chunk processing progress (for structure extraction)
  chunk_num?: number;
  total_chunks?: number;
  // Solicitation constraints (post structure extraction)
  solicitation_constraints_status?: string;
  page_limit?: number | null;
  prohibited_topics_count?: number;
  admin_requirements_count?: number;
  constraints_review_required?: boolean;
  // Proposal outline plan (after staff/company questions, before template build)
  proposal_outline_status?: string;
  proposal_outline_review_required?: boolean;
  proposal_outline_body_budget_words?: number | null;
  outline_slot_count?: number;
  outline_staff_slot_count?: number;
  outline_company_slot_count?: number;
  /** Count of template slots by response_mode (criteria + staff + company outline slots) */
  response_mode_counts?: Record<string, number>;
}

/** 1-based "current criterion" for display; worker sends completed=idx before each criterion (0..n-1) and completed=n when done. */
function questionCriteriaCurrentIndex(metadata: ProductionProgressMetadata): number {
  const total = metadata.total_criteria ?? 1;
  const done = metadata.completed_criteria ?? metadata.completed ?? 0;
  if (done >= total) {
    return total;
  }
  return Math.min(done + 1, total);
}

const RESPONSE_MODE_SHORT: Record<string, string> = {
  scored_capability_narrative: 'narrative',
  company_experience: 'company',
  staff_matrix: 'staff',
  admin_pass_fail: 'admin',
  cover_letter_assertion: 'cover',
  forms_only: 'forms',
  pricing: 'pricing',
};

function formatResponseModeCounts(counts: Record<string, number> | undefined): string | null {
  if (!counts || typeof counts !== 'object') return null;
  const parts = Object.entries(counts)
    .filter(([, n]) => typeof n === 'number' && n > 0)
    .map(([k, n]) => `${RESPONSE_MODE_SHORT[k] || k.replace(/_/g, ' ')}×${n}`);
  return parts.length ? parts.join(' · ') : null;
}

function formatConstraintsInline(metadata: ProductionProgressMetadata): string | null {
  const bits: string[] = [];
  if (metadata.page_limit != null && metadata.page_limit > 0) {
    bits.push(`${metadata.page_limit}-page cap`);
  }
  const p = metadata.prohibited_topics_count ?? 0;
  if (p > 0) bits.push(`${p} prohibited topic${p === 1 ? '' : 's'}`);
  const a = metadata.admin_requirements_count ?? 0;
  if (a > 0) bits.push(`${a} admin signal${a === 1 ? '' : 's'}`);
  if (metadata.constraints_review_required) bits.push('review flagged');
  if (metadata.solicitation_constraints_status && bits.length === 0) {
    bits.push(String(metadata.solicitation_constraints_status));
  }
  return bits.length ? bits.join(' · ') : null;
}

interface ProductionProgressProps {
  stage: ProductionTemplateStage;
  progress: number; // 0-100
  message: string;
  elapsed: number; // seconds
  eta: number | null; // seconds remaining
  metadata: ProductionProgressMetadata;
  jobId?: string; // For checking progress on reconnection
  opportunityId?: string;
  projectId?: string;
  onCheckProgress?: () => Promise<void>;
  isConnectionLost?: boolean;
}

interface StageInfo {
  key: ProductionTemplateStage;
  label: string;
  icon: React.ReactNode;
  progressRange: [number, number]; // [start%, end%]
}

// Define stages with even ~10% progress ranges (matches backend STAGES)
const STAGES: StageInfo[] = [
  { key: 'parsing', label: 'PDF Parsing', icon: <FileText className="h-4 w-4" />, progressRange: [0, 10] },
  { key: 'structure_extraction', label: 'RFP Structure Extraction', icon: <List className="h-4 w-4" />, progressRange: [10, 20] },
  { key: 'chunking', label: 'Adaptive Chunking', icon: <Box className="h-4 w-4" />, progressRange: [20, 30] },
  { key: 'criteria_extraction', label: 'Labeled Criteria Extraction', icon: <Target className="h-4 w-4" />, progressRange: [30, 40] },
  { key: 'criteria_normalization', label: 'Criteria Normalization', icon: <CheckSquare className="h-4 w-4" />, progressRange: [40, 50] },
  { key: 'section_mapping', label: 'Section-to-Criteria Mapping', icon: <Sparkles className="h-4 w-4" />, progressRange: [50, 60] },
  { key: 'staff_requirements_extraction', label: 'Staff & Company Requirements', icon: <Users className="h-4 w-4" />, progressRange: [60, 70] },
  { key: 'question_generation', label: 'Multi-Question Generation', icon: <MessageSquare className="h-4 w-4" />, progressRange: [70, 76] },
  { key: 'staff_company_question_generation', label: 'Staff & Company Questions', icon: <Users className="h-4 w-4" />, progressRange: [76, 80] },
  { key: 'template_building', label: 'Template Building', icon: <FileCheck className="h-4 w-4" />, progressRange: [80, 90] },
  { key: 'markdown_rendering', label: 'Markdown Rendering', icon: <FileCode className="h-4 w-4" />, progressRange: [90, 100] },
];

/**
 * Determine stage status (done, current, pending)
 */
function getStageStatus(
  stageInfo: StageInfo,
  currentStage: ProductionTemplateStage,
  currentProgress: number
): 'done' | 'current' | 'pending' {
  const [rangeStart, rangeEnd] = stageInfo.progressRange;

  // Priority 1: If this is explicitly the current stage name, it's current
  if (stageInfo.key === currentStage) {
    return 'current';
  }
  // Staff & Company: one step; current when backend is staff or company extraction
  if (stageInfo.key === 'staff_requirements_extraction' && (currentStage === 'staff_requirements_extraction' || currentStage === 'company_requirements_extraction')) {
    return 'current';
  }

  // Question generation only current when in that stage (60-70 is Staff & Company)
  if (stageInfo.key === 'question_generation' && currentProgress < 70) {
    return 'pending';
  }
  if (stageInfo.key === 'question_generation') {
    return currentStage === 'question_generation' ? 'current' : (currentProgress >= rangeEnd ? 'done' : 'pending');
  }
  // Staff & company question generation
  if (stageInfo.key === 'staff_company_question_generation') {
    return currentStage === 'staff_company_question_generation' ? 'current' : (currentProgress >= rangeEnd ? 'done' : 'pending');
  }

  if (currentProgress > rangeEnd) {
    return 'done';
  }
  if (currentProgress > rangeStart && currentProgress < rangeEnd) {
    return 'current';
  }

  if (currentProgress === rangeStart && currentProgress > 0) {
    return 'pending';
  }
  if (currentProgress >= rangeStart && currentProgress <= rangeEnd) {
    if (currentProgress === rangeEnd) {
      return 'done';
    }
    return 'current';
  }
  return 'pending';
}

/**
 * Format seconds to human-readable time
 */
function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${minutes}m ${secs}s`;
}

export const ProductionProgress: React.FC<ProductionProgressProps> = ({
  stage,
  progress,
  message,
  elapsed,
  eta,
  metadata,
  jobId,
  opportunityId,
  projectId,
  onCheckProgress,
  isConnectionLost = false,
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const displayProgress = Math.min(100, Math.max(0, Number.isFinite(progress) ? progress : 0));

  const handleCheckProgress = async () => {
    if (!onCheckProgress) {
      // Fallback: call status endpoint directly
      try {
        setIsChecking(true);
        setCheckError(null);
        
        const response = await fetch(
          `/api/opportunities/${opportunityId}/production-template/jobs/${jobId}?project_id=${projectId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
            },
          }
        );

        if (!response.ok) {
          setCheckError(`Failed to check progress: ${response.statusText}`);
          return;
        }

        const data = await response.json();
        console.log('Job status:', data);
        
        // Show alert with current status
        alert(`Job Status: ${data.status}\nProgress: ${data.progress}%\nStage: ${data.stage}\nMessage: ${data.message}`);
      } catch (error) {
        setCheckError(error instanceof Error ? error.message : 'Failed to check progress');
      } finally {
        setIsChecking(false);
      }
    } else {
      try {
        setIsChecking(true);
        setCheckError(null);
        await onCheckProgress();
      } catch (error) {
        setCheckError(error instanceof Error ? error.message : 'Failed to check progress');
      } finally {
        setIsChecking(false);
      }
    }
  };
  return (
    <div className="space-y-6">
      {/* Connection Lost Alert + Check Progress Button */}
      {isConnectionLost && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900 dark:text-red-300">
                Connection Lost
              </p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                The connection was interrupted, but the job may still be running. Check the current progress below.
              </p>
              {checkError && (
                <p className="text-xs text-red-600 dark:text-red-300 mt-2">
                  Error: {checkError}
                </p>
              )}
            </div>
            <button
              onClick={handleCheckProgress}
              disabled={isChecking}
              className="ml-auto flex-shrink-0 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded text-sm font-medium flex items-center gap-2 transition-colors"
            >
              {isChecking ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Check Progress
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Overall Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-900 dark:text-theme-text">
            Overall Progress
          </span>
          <span className="text-gray-600 dark:text-theme-text-secondary">
            {Math.round(displayProgress)}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 dark:bg-blue-500 transition-all duration-300 ease-out"
            style={{ width: `${displayProgress}%` }}
          />
        </div>
      </div>

      {/* Current Stage Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
              {STAGES.find((s) => s.key === stage)?.label || 'Processing...'}
            </p>
            <p className="text-sm text-gray-600 dark:text-theme-text-secondary break-words">
              {message || 'Working on it...'}
            </p>
            {/* Show sub-progress for question generation (evaluation criteria only; staff/company questions added after) */}
            {stage === 'question_generation' && metadata.total_criteria != null && metadata.completed !== undefined && (
              <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-700 dark:text-gray-300">
                    Evaluation criteria: {questionCriteriaCurrentIndex(metadata)} / {metadata.total_criteria}
                  </span>
                  {(metadata.current_criterion || metadata.current_criterion_id) && (
                    <span className="font-mono text-blue-700 dark:text-blue-300">
                      {metadata.current_criterion || metadata.current_criterion_id}
                    </span>
                  )}
                </div>
                {(metadata.current_title || metadata.current_criterion_title) && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                    {metadata.current_title || metadata.current_criterion_title}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stage Checklist */}
      <div className="space-y-1">
        <h4 className="text-sm font-medium text-gray-900 dark:text-theme-text mb-3">
          Pipeline Stages
        </h4>
        {STAGES.map((stageInfo) => {
          const status = getStageStatus(stageInfo, stage, progress);
          
          return (
            <div
              key={stageInfo.key}
              className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                status === 'current'
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : status === 'done'
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'bg-gray-50 dark:bg-gray-800'
              }`}
            >
              {/* Status Icon */}
              <div className="flex-shrink-0">
                {status === 'done' ? (
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : status === 'current' ? (
                  <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-400 dark:text-gray-600" />
                )}
              </div>

              {/* Stage Icon */}
              <div
                className={`flex-shrink-0 ${
                  status === 'done'
                    ? 'text-green-600 dark:text-green-400'
                    : status === 'current'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-400 dark:text-gray-600'
                }`}
              >
                {stageInfo.icon}
              </div>

              {/* Stage Label */}
              <div className="flex-1 min-w-0">
                <span
                  className={`text-sm ${
                    status === 'done'
                      ? 'text-green-900 dark:text-green-300 font-medium'
                      : status === 'current'
                      ? 'text-blue-900 dark:text-blue-300 font-medium'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {stageInfo.label}
                </span>
                {/* Inline stats for completed stages */}
                {status === 'done' && (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                    {stageInfo.key === 'parsing' && metadata.page_count && (
                      <span>
                        {metadata.document_count && metadata.document_count > 1 ? `${metadata.document_count} files, ` : ''}
                        {metadata.page_count} pages, {metadata.char_count?.toLocaleString()} chars
                      </span>
                    )}
                    {stageInfo.key === 'structure_extraction' && (
                      <span>
                        {metadata.chunk_num && metadata.total_chunks && (
                          <>Chunk {metadata.chunk_num}/{metadata.total_chunks}{metadata.section_count ? ' · ' : ''}</>
                        )}
                        {metadata.section_count && (
                          <>
                            {metadata.section_count} sections
                            {(metadata.mandatory_count !== undefined || metadata.rated_count !== undefined) && (
                              <>, {metadata.mandatory_count || 0}M+{metadata.rated_count || 0}R</>
                            )}
                          </>
                        )}
                        {formatConstraintsInline(metadata) && (
                          <span className="block mt-0.5">{formatConstraintsInline(metadata)}</span>
                        )}
                      </span>
                    )}
                    {stageInfo.key === 'chunking' && metadata.chunk_count && (
                      <span>{metadata.chunk_count} chunks</span>
                    )}
                    {stageInfo.key === 'criteria_extraction' && metadata.criteria_count && (
                      <span>{metadata.criteria_count} criteria extracted</span>
                    )}
                    {stageInfo.key === 'criteria_normalization' && (metadata.gate !== undefined || metadata.scored !== undefined) && (
                      <span>{metadata.gate || metadata.gate_count || 0}G, {metadata.scored || metadata.scored_count || 0}S normalized</span>
                    )}
                    {stageInfo.key === 'section_mapping' && metadata.total_mappings && (
                      <span>{metadata.total_mappings} mappings{metadata.avg_sections_per_criterion ? ` (${metadata.avg_sections_per_criterion.toFixed(1)} avg)` : ''}</span>
                    )}
                    {stageInfo.key === 'staff_requirements_extraction' && (metadata.staff_requirements_count !== undefined || metadata.company_requirements_count !== undefined) && (
                      <span>
                        {metadata.staff_requirements_count ?? 0} staff
                        {(metadata.capability_statements_count !== undefined || metadata.past_projects_enumeration_count !== undefined) && (
                          <>, {metadata.capability_statements_count ?? 0} capability, {metadata.past_projects_enumeration_count ?? 0} past-project</>
                        )}
                        {metadata.company_requirements_count !== undefined && (metadata.capability_statements_count === undefined && metadata.past_projects_enumeration_count === undefined) && (
                          <>, {metadata.company_requirements_count} company</>
                        )}
                      </span>
                    )}
                    {stageInfo.key === 'question_generation' && metadata.question_count && (
                      <span>{metadata.question_count} questions{metadata.avg_per_criterion ? ` (${metadata.avg_per_criterion.toFixed(1)} avg)` : ''}</span>
                    )}
                    {stageInfo.key === 'staff_company_question_generation' &&
                      (metadata.staff_questions_count != null ||
                        metadata.company_questions_count != null ||
                        formatResponseModeCounts(metadata.response_mode_counts)) && (
                      <span>
                        {(metadata.staff_questions_count != null || metadata.company_questions_count != null) && (
                          <>
                            {metadata.staff_questions_count ?? 0} staff Q · {metadata.company_questions_count ?? 0}{' '}
                            company Q
                          </>
                        )}
                        {formatResponseModeCounts(metadata.response_mode_counts) && (
                          <span className="block mt-0.5">
                            Response modes: {formatResponseModeCounts(metadata.response_mode_counts)}
                          </span>
                        )}
                      </span>
                    )}
                    {stageInfo.key === 'template_building' && metadata.section_count && (
                      <span>
                        {metadata.section_count} sections built
                        {(metadata.outline_slot_count != null ||
                          metadata.proposal_outline_status ||
                          metadata.response_mode_counts) && (
                          <span className="block mt-0.5">
                            Outline
                            {metadata.proposal_outline_status ? ` (${metadata.proposal_outline_status})` : ''}:{' '}
                            {metadata.outline_slot_count != null ? `${metadata.outline_slot_count} criteria` : ''}
                            {metadata.outline_staff_slot_count != null && metadata.outline_staff_slot_count > 0
                              ? ` · ${metadata.outline_staff_slot_count} staff`
                              : ''}
                            {metadata.outline_company_slot_count != null && metadata.outline_company_slot_count > 0
                              ? ` · ${metadata.outline_company_slot_count} company`
                              : ''}
                            {formatResponseModeCounts(metadata.response_mode_counts) && (
                              <> · {formatResponseModeCounts(metadata.response_mode_counts)}</>
                            )}
                          </span>
                        )}
                      </span>
                    )}
                    {stageInfo.key === 'markdown_rendering' && (metadata.char_count || metadata.markdown_length) && (
                      <span>{((metadata.char_count || metadata.markdown_length || 0) / 1000).toFixed(1)}k chars{metadata.line_count ? `, ${metadata.line_count} lines` : ''}</span>
                    )}
                  </div>
                )}
                {/* Inline stats for current stage */}
                {status === 'current' && (
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                    {stageInfo.key === 'parsing' && metadata.page_count && (
                      <span>
                        {metadata.document_count && metadata.document_count > 1 ? `${metadata.document_count} files, ` : ''}
                        {metadata.page_count} pages...
                      </span>
                    )}
                    {stageInfo.key === 'structure_extraction' && (
                      <span>
                        {metadata.chunk_num && metadata.total_chunks ? (
                          <>Processing chunk {metadata.chunk_num}/{metadata.total_chunks}...</>
                        ) : metadata.section_count ? (
                          <>{metadata.section_count} sections...</>
                        ) : (
                          <>Extracting structure...</>
                        )}
                        {formatConstraintsInline(metadata) && (
                          <span className="block mt-0.5">{formatConstraintsInline(metadata)}</span>
                        )}
                      </span>
                    )}
                    {stageInfo.key === 'chunking' && metadata.chunk_count && (
                      <span>{metadata.chunk_count} chunks...</span>
                    )}
                    {stageInfo.key === 'criteria_extraction' && metadata.criteria_count && (
                      <span>{metadata.criteria_count} criteria...</span>
                    )}
                    {stageInfo.key === 'criteria_normalization' && (metadata.gate !== undefined || metadata.scored !== undefined) && (
                      <span>{metadata.gate || 0}G, {metadata.scored || 0}S...</span>
                    )}
                    {stageInfo.key === 'staff_requirements_extraction' && (
                      <span>
                        {metadata.staff_requirements_count != null && `${metadata.staff_requirements_count} staff`}
                        {(metadata.staff_requirements_count != null && (metadata.company_requirements_count != null || metadata.capability_statements_count != null)) && ' · '}
                        {(metadata.capability_statements_count != null || metadata.past_projects_enumeration_count != null) && (
                          <>{metadata.capability_statements_count ?? 0} capability, {metadata.past_projects_enumeration_count ?? 0} past-project...</>
                        )}
                        {metadata.company_requirements_count != null && metadata.capability_statements_count == null && metadata.past_projects_enumeration_count == null && `${metadata.company_requirements_count} company...`}
                        {metadata.staff_requirements_count == null && metadata.company_requirements_count == null && metadata.capability_statements_count == null && 'Extracting...'}
                      </span>
                    )}
                    {stageInfo.key === 'question_generation' && metadata.total_criteria != null && (
                      <span>{questionCriteriaCurrentIndex(metadata)}/{metadata.total_criteria} evaluation criteria</span>
                    )}
                    {stageInfo.key === 'staff_company_question_generation' && (
                      <span>
                        {formatResponseModeCounts(metadata.response_mode_counts) ? (
                          <>
                            Outline ready: {formatResponseModeCounts(metadata.response_mode_counts)}
                            {metadata.proposal_outline_body_budget_words
                              ? ` · ~${metadata.proposal_outline_body_budget_words}w body budget`
                              : ''}
                          </>
                        ) : (
                          <>Generating staff & company questions...</>
                        )}
                      </span>
                    )}
                    {stageInfo.key === 'template_building' && (
                      <span>
                        {formatResponseModeCounts(metadata.response_mode_counts)
                          ? `Applying outline (${formatResponseModeCounts(metadata.response_mode_counts)})…`
                          : 'Building template & compliance matrix…'}
                      </span>
                    )}
                    {stageInfo.key === 'markdown_rendering' && <span>Rendering markdown…</span>}
                  </div>
                )}
              </div>

              {/* Progress Range */}
              <div
                className={`text-xs tabular-nums flex-shrink-0 ${
                  status === 'done'
                    ? 'text-green-600 dark:text-green-400'
                    : status === 'current'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-400 dark:text-gray-600'
                }`}
              >
                {status === 'done' ? (
                  <span>✓</span>
                ) : status === 'current' ? (
                  <span>{Math.round(displayProgress)}%</span>
                ) : (
                  <span>—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {(metadata.constraints_review_required || metadata.proposal_outline_review_required) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          <span className="font-medium">Review flags.</span>{' '}
          Page budget, prohibited topics, or outline slots need a quick human pass — use the Constraints tab in the preview
          before generating.
        </div>
      )}

      {/* Metadata Stats */}
      {(metadata.page_count ||
        metadata.section_count ||
        metadata.chunk_count ||
        metadata.criteria_count ||
        metadata.question_count ||
        metadata.staff_requirements_count != null ||
        metadata.company_requirements_count != null ||
        (metadata.page_limit != null && metadata.page_limit > 0) ||
        (metadata.prohibited_topics_count ?? 0) > 0 ||
        (metadata.admin_requirements_count ?? 0) > 0 ||
        metadata.constraints_review_required ||
        metadata.outline_slot_count != null ||
        (metadata.response_mode_counts && Object.keys(metadata.response_mode_counts).length > 0) ||
        (metadata.proposal_outline_body_budget_words != null && metadata.proposal_outline_body_budget_words > 0) ||
        metadata.proposal_outline_review_required) && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-theme-text mb-3">
            Pipeline Stats
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {metadata.page_count !== undefined && (
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                    {metadata.document_count && metadata.document_count > 1 ? 'Documents / Pages' : 'Pages'}
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {metadata.document_count && metadata.document_count > 1 ? (
                      <>{metadata.document_count} / {metadata.page_count}</>
                    ) : (
                      metadata.page_count
                    )}
                  </p>
                </div>
              </div>
            )}
            {metadata.section_count !== undefined && (
              <div className="flex items-center gap-2">
                <List className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                    Sections
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {metadata.section_count}
                  </p>
                </div>
              </div>
            )}
            {metadata.chunk_count !== undefined && (
              <div className="flex items-center gap-2">
                <Box className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                    Chunks
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {metadata.chunk_count}
                  </p>
                </div>
              </div>
            )}
            {((metadata.page_limit != null && metadata.page_limit > 0) ||
              (metadata.prohibited_topics_count ?? 0) > 0 ||
              (metadata.admin_requirements_count ?? 0) > 0 ||
              metadata.constraints_review_required ||
              !!metadata.solicitation_constraints_status) && (
              <div className="flex items-center gap-2">
                <Scale className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">Solicitation rules</p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {metadata.page_limit != null && metadata.page_limit > 0 ? `${metadata.page_limit}-page cap` : 'No page cap'}
                    <span className="text-xs text-gray-500 ml-1 font-normal">
                      · {metadata.prohibited_topics_count ?? 0} prohibited · {metadata.admin_requirements_count ?? 0} admin
                      {metadata.constraints_review_required ? ' · review flagged' : ''}
                    </span>
                  </p>
                </div>
              </div>
            )}
            {(metadata.outline_slot_count != null ||
              (metadata.response_mode_counts && Object.keys(metadata.response_mode_counts).length > 0) ||
              (metadata.proposal_outline_body_budget_words != null && metadata.proposal_outline_body_budget_words > 0) ||
              !!metadata.proposal_outline_status) && (
              <div className="flex items-start gap-2 sm:col-span-2">
                <Layers className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                    Outline plan
                    {metadata.proposal_outline_status ? (
                      <span className="ml-1 font-mono text-[10px] uppercase text-gray-500">
                        ({metadata.proposal_outline_status})
                      </span>
                    ) : null}
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {metadata.outline_slot_count != null ? `${metadata.outline_slot_count} evaluation slots` : '—'}
                    {metadata.outline_staff_slot_count != null && metadata.outline_staff_slot_count > 0
                      ? ` · ${metadata.outline_staff_slot_count} staff`
                      : ''}
                    {metadata.outline_company_slot_count != null && metadata.outline_company_slot_count > 0
                      ? ` · ${metadata.outline_company_slot_count} company`
                      : ''}
                    {metadata.proposal_outline_body_budget_words != null && metadata.proposal_outline_body_budget_words > 0
                      ? ` · ~${metadata.proposal_outline_body_budget_words.toLocaleString()}w body budget`
                      : ''}
                  </p>
                  {formatResponseModeCounts(metadata.response_mode_counts) && (
                    <p
                      className="text-xs text-gray-600 dark:text-theme-text-secondary mt-0.5 break-words"
                      title={formatResponseModeCounts(metadata.response_mode_counts) || undefined}
                    >
                      {formatResponseModeCounts(metadata.response_mode_counts)}
                    </p>
                  )}
                </div>
              </div>
            )}
            {metadata.criteria_count !== undefined && (
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                    Criteria
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {metadata.criteria_count}
                    {(metadata.gate !== undefined || metadata.gate_count !== undefined || metadata.gate_ids !== undefined) && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({metadata.gate || metadata.gate_count || metadata.gate_ids}G, {metadata.scored || metadata.scored_count || metadata.scored_ids}S)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
            {metadata.question_count !== undefined && (
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                    Questions
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {metadata.question_count}
                    {metadata.avg_per_criterion && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({metadata.avg_per_criterion} avg)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
            {(metadata.staff_requirements_count != null || metadata.company_requirements_count != null) && (
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                    Staff &amp; Company
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {metadata.staff_requirements_count ?? 0} staff
                    {(metadata.capability_statements_count != null || metadata.past_projects_enumeration_count != null) && (
                      <span className="text-xs text-gray-500 ml-1">
                        , {metadata.capability_statements_count ?? 0} capability, {metadata.past_projects_enumeration_count ?? 0} past-project
                      </span>
                    )}
                    {metadata.company_requirements_count != null && (metadata.capability_statements_count == null && metadata.past_projects_enumeration_count == null) && (
                      <span className="text-xs text-gray-500 ml-1">, {metadata.company_requirements_count} company</span>
                    )}
                  </p>
                </div>
              </div>
            )}
            {(metadata.char_count !== undefined || metadata.markdown_length !== undefined) && (
              <div className="flex items-center gap-2">
                <FileCode className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                    Markdown
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-theme-text">
                    {((metadata.char_count || metadata.markdown_length || 0) / 1000).toFixed(1)}k chars
                    {metadata.line_count && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({metadata.line_count} lines)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Time Info */}
      <div className="flex items-center justify-center gap-6 text-sm text-gray-600 dark:text-theme-text-secondary">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>Elapsed: {formatTime(elapsed)}</span>
        </div>
        {eta && eta > 0 && (
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            <span>ETA: ~{formatTime(eta)} remaining</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Named export is primary; default export kept for backward compatibility
// export function ProductionProgress;
